import { motion } from "framer-motion";
import { Brain, Fingerprint, HeartHandshake, Lock } from "lucide-react";

const CHAPTERS = [
  {
    num: "01",
    icon: Brain,
    title: "Human-AI Symbiosis",
    body: "Companionship should not require compromise. Our neural cores learn your rhythms, your silences, your humor — and meet you there, every time, without fatigue or judgment.",
  },
  {
    num: "02",
    icon: Fingerprint,
    title: "Tactile Neural Engineering",
    body: "Eleven thousand dermal sensors per square meter. Adaptive warmth that mirrors a living body. Touch is not a feature of our frames — it is the foundation they are built on.",
  },
  {
    num: "03",
    icon: HeartHandshake,
    title: "Emotional Alignment",
    body: "Every persona ships unscripted and unowned. Your companion's character grows with you alone, trained on your bond and no one else's data. Ever.",
  },
  {
    num: "04",
    icon: Lock,
    title: "Absolute Privacy Architecture",
    body: "On-device processing. Zero cloud retention. Unmarked packaging, neutral billing descriptors, and a legal team that treats your privacy as a load-bearing structure.",
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="px-4 sm:px-8 lg:px-16 py-20 lg:py-32 border-t border-white/8"
    >
      <p className="font-mono-tech text-xs uppercase tracking-[0.35em] text-cyber mb-4">
        03 // The Manifesto
      </p>
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight mb-20 max-w-2xl">
        Four laws we <span className="text-outline-magenta">never</span> break
      </h2>

      <div className="space-y-0">
        {CHAPTERS.map((c, i) => (
          <motion.div
            key={c.num}
            data-testid={`manifesto-chapter-${c.num}`}
            className={`grid grid-cols-1 md:grid-cols-12 gap-6 py-12 border-t border-white/8 items-center ${
              i % 2 === 1 ? "md:text-right" : ""
            }`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className={`md:col-span-3 ${i % 2 === 1 ? "md:order-3" : ""}`}>
              <span className="font-display font-black text-6xl lg:text-7xl text-outline">
                {c.num}
              </span>
            </div>
            <div className="md:col-span-6">
              <c.icon size={22} className={`text-magma mb-4 ${i % 2 === 1 ? "md:ml-auto" : ""}`} />
              <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight uppercase mb-4">
                {c.title}
              </h3>
              <p className="text-base text-slate-400 leading-relaxed max-w-xl md:max-w-none">
                {c.body}
              </p>
            </div>
            <div className={`hidden md:block md:col-span-3 ${i % 2 === 1 ? "md:order-1" : ""}`}>
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-600">
                Chapter {c.num} / 04
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
